import { Component, inject, OnInit, signal } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isSameDay } from '@siat-fe/core';
import {
  CustomValidators,
  DatepickerFieldComponent,
  formatHHmm,
  InputFieldComponent,
  Item,
  LoadingSpinnerComponent,
  RadioButton,
  RadioFieldComponent,
  roundUpToNext15Min,
  SelectFieldComponent,
  SelectTipologicaFieldComponent,
  SnackbarService,
  TextareaFieldComponent,
  TimepickerFieldComponent,
  UploadedFile,
  UploadFileComponent,
} from '@siat-fe/shared';
import { finalize } from 'rxjs';
import {
  DispositivoSupporto,
  IdentificazionePaziente,
  SistemaOperativoApp,
  TicketAllegatoDTO,
  TicketSupportoDTO,
  TicketTipologica,
} from '../../models/ticket-supporto.model';
import { SupportoService } from '../../services/supporto.service';

/**
 * Device options, the first one is the default.
 */
const DISPOSITIVO_ITEMS: Item[] = [
  { id: 'Portale', name: 'Portale' },
  { id: 'App', name: 'App' },
];

/**
 * Requires at least one between the phone and email controls.
 * @returns {ValidationErrors | null} The contattoRequired error, if both are empty
 */
function contattoRequiredValidator(
  group: AbstractControl,
): ValidationErrors | null {
  const phone = group.get('phone')?.value?.trim();
  const email = group.get('email')?.value?.trim();
  return phone || email ? null : { contattoRequired: true };
}

/**
 * Requires a value picked from the tipologica options, not just typed text.
 * @returns {ValidationErrors | null} The required error, if no option is selected
 */
function tipologicaSelectedValidator(
  control: AbstractControl,
): ValidationErrors | null {
  return typeof control.value === 'object' && control.value?.id != null
    ? null
    : { required: true };
}

@UntilDestroy()
@Component({
  selector: 'siat-supporto-dialog',
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    LoadingSpinnerComponent,
    InputFieldComponent,
    RadioFieldComponent,
    TextareaFieldComponent,
    SelectFieldComponent,
    SelectTipologicaFieldComponent,
    DatepickerFieldComponent,
    TimepickerFieldComponent,
    UploadFileComponent,
  ],
  templateUrl: './supporto-dialog.component.html',
})
export class SupportoDialogComponent implements OnInit {
  /**
   * injected services
   */
  readonly activeModal = inject(MatDialogRef<SupportoDialogComponent>);
  private readonly fb = inject(FormBuilder);
  private readonly supportoService = inject(SupportoService);
  private readonly snackbarService = inject(SnackbarService);

  /**
   * The support request form.
   */
  readonly form = this.fb.group(
    {
      phone: ['', [CustomValidators.invalidPhoneNumber]],
      email: ['', [CustomValidators.invalidEmail]],
      identificazione: ['codiceFiscale' as IdentificazionePaziente],
      patientFiscalcode: ['', [CustomValidators.invalidCf]],
      patientFirstName: [''],
      patientLastName: [''],
      ticketType: [
        null as TicketTipologica | string | null,
        [Validators.required, tipologicaSelectedValidator],
      ],
      ticketSystem: [
        null as TicketTipologica | string | null,
        [Validators.required, tipologicaSelectedValidator],
      ],
      problemDescription: ['', [Validators.required]],
      device: [
        DISPOSITIVO_ITEMS[0] as Item | string | null,
        [Validators.required],
      ],
      deviceType: [null as SistemaOperativoApp | null],
      data: [null as Date | null, [Validators.required]],
      ora: [
        null as Date | null,
        [Validators.required, CustomValidators.maxTimeTodayValidator('data')],
      ],
      allegati: [[] as UploadedFile[]],
    },
    { validators: contattoRequiredValidator },
  );

  /**
   * Patient identification options.
   */
  readonly identificazioneItems: RadioButton[] = [
    { id: 'codiceFiscale', name: 'Codice fiscale' },
    { id: 'nomeCognome', name: 'Cognome e nome' },
  ];

  /**
   * Device options.
   */
  readonly dispositivoItems = DISPOSITIVO_ITEMS;

  /**
   * App operating system options.
   */
  readonly sistemaOperativoItems: RadioButton[] = [
    { id: 'iOS', name: 'iOS' },
    { id: 'Android', name: 'Android' },
  ];

  /**
   * Selected device.
   */
  readonly dispositivo = signal<DispositivoSupporto>('Portale');

  /**
   * Maximum selectable date: today.
   */
  readonly maxDate = new Date();

  /**
   * Maximum selectable 'HH:mm' time; set only when the selected date is today.
   */
  readonly maxTime = signal<string | null>(null);

  /**
   * Selected patient identification mode.
   */
  readonly identificazione = signal<IdentificazionePaziente>('codiceFiscale');

  /**
   * True while the ticket is being sent.
   */
  readonly isSending = signal(false);

  /**
   * @inheritDoc
   */
  ngOnInit(): void {
    this.watchIdentificazioneChanges();
    this.watchDispositivoChanges();
    this.watchDataChanges();
  }

  /**
   * True when both contacts are empty and the user already interacted with them.
   * @returns {boolean} Whether to show the missing contact error
   */
  get showContattoError(): boolean {
    const { phone, email } = this.form.controls;
    return (
      this.form.hasError('contattoRequired') && (phone.touched || email.touched)
    );
  }

  /**
   * Validates the form and sends the support ticket.
   */
  sendTicket(): void {
    if (this.form.invalid || this.isSending()) {
      this.form.markAllAsTouched();
      return;
    }
    this.submitTicket(this.buildReport());
  }

  /**
   * Switches the patient fields on every identification mode change.
   */
  private watchIdentificazioneChanges(): void {
    this.form.controls.identificazione.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((value) => this.applyIdentificazione(value));
  }

  /**
   * Shows or hides the app operating system on every device change.
   */
  private watchDispositivoChanges(): void {
    this.form.controls.device.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((value) => this.applyDispositivo(value));
  }

  /**
   * Updates the ora limits and validity on every data change.
   */
  private watchDataChanges(): void {
    this.form.controls.data.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((data) => this.applyOraLimits(data));
  }

  /**
   * Stores the identification mode and clears the fields of the other mode.
   */
  private applyIdentificazione(value: IdentificazionePaziente | null): void {
    const identificazione = value ?? 'codiceFiscale';
    this.identificazione.set(identificazione);
    const { patientFiscalcode, patientFirstName, patientLastName } =
      this.form.controls;
    if (identificazione === 'codiceFiscale') {
      patientFirstName.reset('');
      patientLastName.reset('');
    } else {
      patientFiscalcode.reset('');
    }
  }

  /**
   * Stores the device, requiring the operating system only for the app.
   */
  private applyDispositivo(value: Item | string | null): void {
    const dispositivo = typeof value === 'object' ? value?.id : value;
    if (
      (dispositivo !== 'Portale' && dispositivo !== 'App') ||
      dispositivo === this.dispositivo()
    ) {
      return;
    }
    this.dispositivo.set(dispositivo);
    this.toggleRequired(this.form.controls.deviceType, dispositivo === 'App');
  }

  /**
   * Adds or removes the required validator on a control, resetting it.
   */
  private toggleRequired(control: AbstractControl, required: boolean): void {
    if (required) {
      control.addValidators(Validators.required);
    } else {
      control.removeValidators(Validators.required);
    }
    control.reset();
  }

  /**
   * Limits ora to now when the selected data is today, then revalidates it.
   */
  private applyOraLimits(data: Date | null): void {
    this.maxTime.set(
      data && isSameDay(new Date(data), new Date())
        ? formatHHmm(roundUpToNext15Min(new Date()))
        : null,
    );
    this.form.controls.ora.updateValueAndValidity();
  }

  /**
   * Sends the ticket and closes the dialog on success.
   */
  private submitTicket(report: TicketSupportoDTO): void {
    this.isSending.set(true);
    this.supportoService
      .createTicket(report)
      .pipe(
        finalize(() => this.isSending.set(false)),
        untilDestroyed(this),
      )
      .subscribe({
        next: () => {
          this.snackbarService.showSuccessSnackbar(
            'Segnalazione inviata correttamente',
          );
          this.activeModal.close(true);
        },
        error: () =>
          this.snackbarService.showDangerSnackbar(
            "Errore durante l'invio della segnalazione",
          ),
      });
  }

  /**
   * Builds the ticket report from the form, sending only the filled fields.
   * @returns {TicketSupportoDTO} The ticket report
   */
  private buildReport(): TicketSupportoDTO {
    const value = this.form.getRawValue();
    const problemDateTime = new Date(value.data as Date);
    problemDateTime.setHours(
      (value.ora as Date).getHours(),
      (value.ora as Date).getMinutes(),
      0,
      0,
    );
    const isCodiceFiscale = value.identificazione === 'codiceFiscale';
    return {
      phone: value.phone?.trim() || undefined,
      email: value.email?.trim() || undefined,
      patientFiscalcode: isCodiceFiscale
        ? value.patientFiscalcode?.trim().toUpperCase() || undefined
        : undefined,
      patientFirstName: isCodiceFiscale
        ? undefined
        : value.patientFirstName?.trim() || undefined,
      patientLastName: isCodiceFiscale
        ? undefined
        : value.patientLastName?.trim() || undefined,
      problemDescription: (value.problemDescription ?? '').trim(),
      device: this.dispositivo().toLowerCase(),
      deviceType:
        this.dispositivo() === 'App'
          ? (value.deviceType ?? undefined)
          : undefined,
      problemDateTime: problemDateTime.toISOString(),
      createdAt: new Date().toISOString(),
      ticketType: (value.ticketType as TicketTipologica).name,
      ticketSystem: (value.ticketSystem as TicketTipologica).name,
      attachments: (value.allegati ?? []).map((allegato) =>
        this.toAllegato(allegato),
      ),
    };
  }

  /**
   * Converts an uploaded file into a ticket attachment with pure base64 content.
   * @returns {TicketAllegatoDTO} The attachment to send
   */
  private toAllegato(allegato: UploadedFile): TicketAllegatoDTO {
    return {
      file: String(allegato.file ?? '').split(',')[1] ?? '',
      nomeFile: allegato.nomeFile,
      type: allegato.type,
    };
  }
}
